import { useAppDispatch, useAppSelector } from "@/common/reducers/hooks/store.hook";
import MessageEditorControlled from "@common-components/message-editor/message-editor-controlled";
import {
    setCancelIfReceived,
    setScheduleText,
    setShowRecurrence
} from "@common/reducers/ScheduledMessageEditorReducer";
import { handleAddScheduledMessageCloseThunk } from "@common/thunks/UserMessagesThunks";
import {
    Box,
    FormControlLabel,
    useMediaQuery,
} from '@mui/material';
import { BTCheckbox } from "@common-components/atoms/bt-checkbox/bt-checkbox";
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useEffect, useMemo, useState } from "react";
import AddScheduledMessageDialogFooter from "./add-schedulued-message-dialog-footer";
import composeStyles from "./compose-dialog.module.scss";
import { MessageSquareOff } from "lucide-react";
import { Tooltip as UiTooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ScheduleSummary } from "./schedule-summary";
import { SendMessageTo } from "./send-message-to/send-message-to";
import PreviewScheduleMessage from "./preview-schedule-message";
import MessageRecurrenceOptions from "@common/components/schedule-picker/message-recurrence-options";
import MessageDeliveryOptions from "@common/components/schedule-picker/message-delivery-options";
import { generateRecurrenceText, getRuleOptionsSafe, WeekStartDay } from "@common/components/schedule-picker/recurrence-utils";
import { getUserStartDayOfWeek } from "@common/reducers/UserSelectors";
import { setRecurrenceText } from "@common/reducers/ScheduledMessageEditorReducer";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useClockPreference } from '@common/hooks/use-clock-preference';
import { namespace } from '@common/config/constants';
import { selectScheduleRecipients } from '@common-reducers/AddScheduledMessageButtonReducer';
import { hasStatusRecipient } from '@common/scheduling/status-recipient';

/**
 * FE#384 — Responsive "two-pane tabs" Schedule Message editor.
 *
 * The old layout put the composer + WhatsApp preview side-by-side and only
 * degraded at MUI `md`; on 10–11" laptops the modal overflowed the viewport, the
 * composer collapsed and the Schedule-Send footer fell off screen. This rebuilds
 * the dialog as a height-capped modal whose middle region is a vertical tab rail
 * (Recipients / Message / Timing / Options) with a pinned header + footer, per the
 * design handoff. The Message tab's composer FILLS all remaining height at every
 * resolution (MessageEditorControlled `fillHeight`).
 *
 * The WhatsApp preview docks beside the modal at ≥ PREVIEW_DOCK_MIN_PX and, below
 * that, is reached through the header "Preview" button as a slide-in sheet.
 */

/** The single responsive threshold: at/above this the preview docks beside the
 *  modal; below it the modal is fluid and the preview is a toggled sheet. */
export const PREVIEW_DOCK_MIN_PX = 1200;

/** At/above this VIEWPORT height the dialog drops the tab rail and stacks all
 *  three sections (Message / Timing / Options) at once; below it, the tabbed
 *  compact layout is used. Sized for the fixed chrome (header + recipients row +
 *  summary + footer ≈ 300px) plus a ~6-line composer and the Timing/Options
 *  blocks stacked. */
export const TALL_LAYOUT_MIN_PX = 760;

type ScheduledMessageEditorProps = {
    isPreview?: boolean;
    editorContext: 'scheduleMessage' | 'scheduleCampaign';
}

export default function ScheduledMessageEditor({
    isPreview,
    editorContext,
}: ScheduledMessageEditorProps) {
    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const isRtl = i18n.dir(i18n.language) === 'rtl';

    // The one tunable threshold (see PREVIEW_DOCK_MIN_PX). `isShort` tightens
    // padding on short laptop panels (e.g. 1366×768 with browser chrome → ~640).
    const isWide = useMediaQuery(`(min-width:${PREVIEW_DOCK_MIN_PX}px)`);
    const isShort = useMediaQuery('(max-height:640px)');

    // Height-based responsive layout. The tabs exist ONLY to save vertical space,
    // so they are surfaced only under height pressure. When the viewport is tall
    // enough to show every section at once (~room for 5-6+ composer lines plus the
    // Timing + Options blocks), render the FULL untabbed dialog: all three sections
    // (Message / Timing / Options) stacked and visible, with no left tab rail.
    // Below the threshold, collapse to the tab rail (current compact layout).
    const isTall = useMediaQuery(`(min-height:${TALL_LAYOUT_MIN_PX}px)`);

    // Compact-only preview sheet. Never shown when the preview is docked (wide).
    const [previewOpen, setPreviewOpen] = useState(false);

    const isPoll = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isPoll);
    const recurrenceText = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.recurrenceText);
    const cancelIfReceived = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.cancelIfReceived);
    // A Status has no conversation to receive a reply in: "Cancel when a message is received" doesn't apply.
    const isStatus = hasStatusRecipient(useAppSelector(selectScheduleRecipients));
    const dueDate = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.dueDate);
    const rruleset = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.rruleset);
    const isRecurring = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isRecurring);
    const startDayAtWeek = useAppSelector(getUserStartDayOfWeek);
    const clockPreference = useClockPreference();

    // This dialog splits the schedule fields across the Timing/Options tabs and no
    // longer mounts <ScheduleSettings> (which was the SOLE dispatcher of
    // setRecurrenceText). Reproduce its side-effect here so the human-readable
    // recurrence summary (recurrenceText → scheduleText → displayScheduleText, and
    // any other consumer) stays fresh. Payload fields (dueDate/rruleset/isRecurring)
    // are produced by the still-mounted individual pickers.
    const computedRecurrenceText = useMemo(() => {
        const options = getRuleOptionsSafe(rruleset, dueDate);
        return generateRecurrenceText(
            { dueDate, isRecurring, rruleset, cancelIfReceived },
            options,
            startDayAtWeek as WeekStartDay,
            t as (key: string, options?: any) => string,
            i18n.language,
            clockPreference,
        );
    }, [dueDate, isRecurring, rruleset, cancelIfReceived, startDayAtWeek, t, i18n.language, clockPreference]);

    useEffect(() => {
        dispatch(setRecurrenceText(computedRecurrenceText));
        moment.updateLocale('en', { week: { dow: startDayAtWeek } });
    }, [computedRecurrenceText, startDayAtWeek, dispatch]);

    // Set showRecurrence to true for this editor context
    useEffect(() => {
        dispatch(setShowRecurrence(true));
    }, [dispatch]);

    // Keep the persisted scheduleText in sync (unchanged behaviour).
    useEffect(() => {
        if (recurrenceText) {
            const text = isPoll ? recurrenceText.replace('message', 'poll') : recurrenceText;
            dispatch(setScheduleText(text));
        }
    }, [isPoll, recurrenceText, dispatch]);

    const onClose = () => dispatch(handleAddScheduledMessageCloseThunk());

    // ONE spacing unit for the whole card: the recipients header, every section
    // and the pinned footer all inset by it, and the gaps between blocks use it
    // too, so no edge reads tighter than another.
    const space = isShort ? 14 : 20;
    const sectionPad = `${space}px`;

    // ── Panes ────────────────────────────────────────────────────────────────
    // No headings and no divider lines: blocks are separated by space alone.
    // Panes carry only the inline inset; the vertical rhythm is the column's
    // `gap` (between panes) plus the header's / footer's own inset (at the
    // ends), all the same `space` unit.
    const paneBase = (visible: boolean, fill = false) => ({
        display: visible ? 'flex' : 'none',
        flexDirection: 'column' as const,
        ...(fill ? { flex: 1, minHeight: 0 } : {}),
        paddingInline: sectionPad,
        // The editor root carries its own 15px bottom margin for other hosts;
        // here the column gap is the only spacing.
        '& .bt-msg-editor-root': { marginBottom: '0 !important' },
    });

    // Compact layout: the three options as icons at the end of the "will be
    // sent on" strip (label in tooltip + aria), replacing the old tab rail.
    const iconToggleClass = (on: boolean) => cn(
        "grid h-8 w-8 place-items-center rounded-full border transition-all duration-200",
        on
            ? "border-[var(--cmp-brand)] bg-[var(--cmp-brand-soft)] text-[var(--cmp-brand-ink)]"
            : "border-transparent bg-transparent text-[var(--tasks-text-secondary)] hover:bg-[var(--color-surface-muted)]",
    );
    const compactOptions = (
        <>
            <MessageRecurrenceOptions iconOnly />
            <MessageDeliveryOptions iconOnly />
            {editorContext === 'scheduleMessage' && !isPoll && !isStatus && (
                // The same (Radix) tooltip as the two pickers beside it — it layers
                // above the dialog; MUI's Tooltip sat underneath it.
                <UiTooltip>
                    <TooltipTrigger asChild>
                        <button
                            type="button"
                            aria-pressed={!!cancelIfReceived}
                            aria-label={t('messageScheduling.cancelWhenReceived')}
                            onClick={() => dispatch(setCancelIfReceived(!cancelIfReceived))}
                            className={iconToggleClass(!!cancelIfReceived)}
                        >
                            <MessageSquareOff className="h-[18px] w-[18px]" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{t('messageScheduling.cancelWhenReceived')}</p>
                    </TooltipContent>
                </UiTooltip>
            )}
        </>
    );

    const modalCard = (
        <Box
            dir={isRtl ? 'rtl' : 'ltr'}
            sx={{
                // The core fix: a flex column capped to the dialog (viewport) height.
                height: '100%',
                minHeight: 0,
                // Wide: 780px when the viewport has it, yielding back toward the
                // docked preview's side as the screen narrows (flex-shrink).
                // (An explicit width, not flex-basis: the dialog is width:auto and
                // sizes to its content, which ignores a basis and collapsed it.)
                width: isWide ? 780 : '100%',
                flexShrink: 1,
                minWidth: 0,
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--color-surface)',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 24px 60px rgba(16,24,40,0.30)',
                // Above the docked preview: its 60px shadow blur otherwise paints
                // a grey band down this card's right edge.
                position: 'relative',
                zIndex: 1,
            }}
        >
            {/* Pinned recipients row — always visible (required for every send), and
                now the TOP row of the dialog: the redundant title header band was
                removed entirely to reclaim its height. The "Schedule a [Message|Status]"
                toggle (rendered by SendMessageTo) conveys the title, and the close
                button (+ the compact Preview toggle) moved into this row's top corner. */}
            <Box sx={{
                position: 'relative',
                flex: 'none',
                // Same inset on all four sides. The close/preview cluster only
                // shares the first line with the narrow Message|Status toggle, so
                // the search field below can run the full width.
                padding: sectionPad,
                background: 'var(--color-surface)',
            }}>
                {/* Top-corner actions, moved out of the removed header band. */}
                <Box sx={{
                    position: 'absolute',
                    // Centred on the toggle row (≈30px tall at the top inset).
                    insetBlockStart: space,
                    insetInlineEnd: space - 6,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    {!isWide && (
                        <Box
                            role="button"
                            onClick={() => setPreviewOpen(v => !v)}
                            sx={{
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 12px',
                                borderRadius: '999px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                userSelect: 'none',
                                border: previewOpen ? '1px solid var(--color-accent)' : '1px solid var(--tasks-cell-border)',
                                background: previewOpen ? 'var(--tasks-menu-hover-bg)' : 'var(--color-surface)',
                                color: previewOpen ? 'var(--color-accent)' : 'var(--color-text)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {previewOpen
                                ? t('messageScheduling.hidePreview', 'Hide preview')
                                : t('messageScheduling.preview', 'Preview')}
                        </Box>
                    )}
                    <Box
                        role="button"
                        aria-label={t('messageScheduling.cancel', 'Close')}
                        onClick={onClose}
                        sx={{
                            width: 30, height: 30,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: '8px', cursor: 'pointer', color: 'var(--color-text-secondary, #7A8698)',
                            '&:hover': { background: 'var(--tasks-menu-hover-bg)' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </Box>
                </Box>
                {/* SendMessageTo renders the "Schedule a [Message|Status]" toggle,
                    the recipient field/chips AND the "scheduled individually"
                    helper line itself — no extra helper here. */}
                <SendMessageTo gap={sectionPad} />
            </Box>

            {/* Body (fills; only this region scrolls) */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                {/* Content pane (the only scroll region). Tall: the editor and
                    the options group stack. Compact (short screens): the editor
                    alone — the options ride as icons on the "will be sent on"
                    strip in the footer, so no tab rail is needed. */}
                <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: sectionPad,
                }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        {/* Message — fills the column's spare height. */}
                        <Box className={composeStyles.composeEditor} sx={{
                            ...paneBase(true, true),
                            // Tall: the editor takes the column's spare height
                            // instead of leaving a blank band above the footer —
                            // but never below ~4 lines (then the column scrolls).
                            ...(isTall ? { minHeight: 240 } : {}),
                        }}>
                            <MessageEditorControlled
                                isPreview={isPreview}
                                editorContext={editorContext}
                                fillHeight
                            />
                        </Box>

                        {/* Options — the "when & how" group, 12px apart inside:
                            the "will be sent on <date> at <time>" sentence (the
                            date/time input — see ScheduleSummary), then how-often
                            + how-to-deliver, then cancel-if-received. Tall layout
                            only; compact uses `compactOptions` in the footer. */}
                        {isTall && (
                        <Box sx={{ ...paneBase(true), gap: '12px' }}>
                            <ScheduleSummary plain />
                            <Box className={composeStyles.composeOptions}>
                                <MessageRecurrenceOptions />
                                <MessageDeliveryOptions />
                            {/* FE#384 — "Cancel when a message is received" lives here
                                now (moved off the composer footer to reclaim a row).
                                scheduleMessage + non-poll only, as before. */}
                            {editorContext === 'scheduleMessage' && !isPoll && !isStatus && (
                                <UiTooltip>
                                    <TooltipTrigger asChild>
                                    <FormControlLabel
                                        sx={{
                                            // Box edge = the square's edge: the
                                            // checkbox's 9px ripple padding is
                                            // removed, so it lines up with the
                                            // chips at the section inset.
                                            m: 0, marginInlineStart: '8px', gap: '8px',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '13px', color: 'var(--color-text)', lineHeight: 1.3,
                                            },
                                        }}
                                        control={
                                            <BTCheckbox
                                                checked={cancelIfReceived || false}
                                                onChange={(e) => dispatch(setCancelIfReceived(e.target.checked))}
                                                color="primary"
                                                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: '18px', color: 'var(--color-text)' } }}
                                            />
                                        }
                                        label={t('messageScheduling.cancelWhenReceived')}
                                        labelPlacement="end"
                                    />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{t('messageScheduling.cancelWhenReceivedHint', 'Cancel the message if a message is received from the contact after it was scheduled')}</p>
                                    </TooltipContent>
                                </UiTooltip>
                            )}
                            </Box>
                        </Box>
                        )}
                    </LocalizationProvider>
                </Box>
            </Box>

            {/* Footer (pinned; never scrolls away). FE#385's ScheduleSummary — the
                "what will happen" strip (who / when / past-time warning + recipient
                fan-out) — sits directly above Send, where the decision is made and
                always visible regardless of the active tab. */}
            <Box sx={{
                flex: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: 'var(--color-surface)',
                /* The inset lives HERE, on the pinned block, so the summary
                   strip, the blocker line and the buttons all share one column.
                   It used to sit on the summary's own wrapper only, and the
                   footer (whose padding is vertical by design — it was written
                   for a scroll container that supplied the inset) ran flush to
                   the card: "Please add at least one recipient" hung off the
                   left edge and Schedule Send touched the right. */
                p: sectionPad,
            }}>
                {!isTall && <ScheduleSummary actions={compactOptions} />}
                <AddScheduledMessageDialogFooter />
            </Box>
        </Box>
    );

    // ── Preview: docked (wide) or slide-in sheet (compact) ─────────────────────
    const dockedPreview = isWide && (
        <Box sx={{
            flex: 'none',
            width: 344,
            alignSelf: 'stretch',
            maxHeight: '100%',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(16,24,40,0.24)',
        }}>
            <PreviewScheduleMessage />
        </Box>
    );

    const previewSheet = !isWide && previewOpen && (
        <>
            <Box
                onClick={() => setPreviewOpen(false)}
                sx={{ position: 'absolute', inset: 0, background: 'rgba(16,24,40,0.34)', zIndex: 5 }}
            />
            <Box sx={{
                position: 'absolute',
                insetBlock: 0,
                insetInlineEnd: 0,
                width: 330,
                maxWidth: '86%',
                background: 'var(--color-surface)',
                boxShadow: '-14px 0 40px rgba(16,24,40,0.22)',
                zIndex: 6,
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Thin close bar only — PreviewScheduleMessage renders its own
                    "WhatsApp Preview" title, so we don't duplicate it here. */}
                <Box sx={{
                    flex: 'none',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '6px 6px 0',
                    position: 'absolute',
                    insetInlineEnd: 4,
                    insetBlockStart: 4,
                    zIndex: 1,
                }}>
                    <Box
                        role="button"
                        aria-label={t('messageScheduling.cancel', 'Close')}
                        onClick={() => setPreviewOpen(false)}
                        sx={{
                            width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: '8px', cursor: 'pointer', color: 'var(--color-text-secondary, #7A8698)',
                            '&:hover': { background: 'var(--tasks-menu-hover-bg)' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </Box>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <PreviewScheduleMessage />
                </Box>
            </Box>
        </>
    );

    return (
        <Box
            dir={isRtl ? 'rtl' : 'ltr'}
            sx={{
                // Root two-pane row. `position: relative` anchors the compact
                // preview sheet. Height is owned by the DialogContent (viewport-cap).
                position: 'relative',
                height: '100%',
                minHeight: 0,
                width: '100%',
                display: 'flex',
                gap: isWide ? '16px' : 0,
                justifyContent: 'center',
                alignItems: 'stretch',
            }}
        >
            {modalCard}
            {dockedPreview}
            {previewSheet}
        </Box>
    );
}
